/**
 * Módulo `auth` — terminal CRT (Fase 3f-a v2).
 *
 * Una sola "pantalla" reutilizable para `/login` y `/registro`:
 *  - `initLogin()`    arranca con la vista MENU (default).
 *  - `initRegister()` arranca directamente en el flujo de REGISTRO.
 *
 * Si el usuario llega autenticado (cookie de sesión válida), redirige
 * inmediatamente a `/` sin pintar nada — la pantalla es de "acceso",
 * no tiene sentido mostrarla a quien ya pasó.
 */

import * as api from './api.js';
import { attachUserGestureBoot } from './audio.js';
import { renderCaseShell } from './case-template.js';
import {
  loadStoredProfile,
  loadStoredTimeOfDay,
  wireAudioToggle,
  wireCrtControls,
} from './crt-controls.js';
import { isPasswordView } from './flows.js';
import { detectActiveSubstitutions } from './font-substitutions.js';
import { attachInputHandler } from './input-handler.js';
import { applyProfile, getProfile } from './monitor-profiles.js';
import { renderTerminal, tickDatetime } from './render.js';
import { createState, type ViewId } from './state.js';

const ROOT_SELECTOR = '#auth-root';

export function initLogin(): void {
  whenReady(() => {
    // La sala enlaza `/login.html?goto=about` para abrir directamente
    // el submenú ACERCA DE (textos legales).
    const goto = new URLSearchParams(window.location.search).get('goto');
    boot(goto === 'about' ? 'about' : 'menu');
  });
}

export function initRegister(): void {
  whenReady(() => boot('register_callsign'));
}

/**
 * Arranca `/verify-email.html`. Con `?token=` verifica automáticamente;
 * sin token entra al formulario de reenvío del enlace.
 */
export function initVerifyEmail(): void {
  whenReady(() => {
    const token = new URLSearchParams(window.location.search).get('token');
    boot(token ? 'verify_email_checking' : 'verify_email_resend', {
      verifyToken: token ?? undefined,
    });
  });
}

/**
 * Arranca `/recovery.html`. Con `?token=` pide la nueva contraseña; sin
 * token pide el email para enviar el enlace de restablecimiento.
 */
export function initRecovery(): void {
  whenReady(() => {
    const token = new URLSearchParams(window.location.search).get('token');
    boot(token ? 'recovery_set_password' : 'recovery_request', {
      resetToken: token ?? undefined,
    });
  });
}

async function boot(
  initialView: ViewId,
  tokens: { verifyToken?: string; resetToken?: string } = {},
): Promise<void> {
  const root = document.querySelector<HTMLElement>(ROOT_SELECTOR);
  if (!root) {
    console.error(`[auth] no se encontró ${ROOT_SELECTOR} en el DOM.`);
    return;
  }

  // 1) Aplicar perfil de monitor antes del primer paint. Leemos la
  //    preferencia guardada en localStorage; si no hay nada, cae al
  //    DEFAULT_PROFILE_ID (mono-p1 verde) gracias a loadStoredProfile.
  const profileId = loadStoredProfile();
  applyProfile(profileId);

  // 2) ¿Sesión ya activa? La hidratamos en el estado y forzamos el
  //    menú principal (entrar al flujo de registro estando logueado
  //    no tiene sentido). El usuario verá la opción 1 como LOGOUT.
  const existing = await api.me();
  let authenticatedUser = null;
  let resolvedView = initialView;
  if (existing.ok) {
    authenticatedUser = existing.data.user;
    // Estando logueado, el flujo de registro no tiene sentido → menú.
    // ACERCA DE sí: es informativo, se respeta aunque haya sesión.
    resolvedView = initialView === 'about' ? 'about' : 'menu';
  }

  // 3) Pintar el case CRT.
  root.classList.add('crt-case');
  root.innerHTML = renderCaseShell();

  const screen = root.querySelector<HTMLElement>('#screen');
  const terminal = root.querySelector<HTMLElement>('#terminal');
  const audioToggle = root.querySelector<HTMLButtonElement>('#audio-toggle');
  if (!screen || !terminal || !audioToggle) {
    console.error('[auth] estructura del case CRT incompleta.');
    return;
  }

  // 4) Conectar controles físicos + audio toggle. El boot por gesto
  //    recibe el sample del perfil activo (si lo tiene) para arrancar
  //    el ambiente coherente sin esperar a otro cambio.
  wireCrtControls(screen, root);
  wireAudioToggle(audioToggle);
  attachUserGestureBoot(getProfile(profileId).ambientSample ?? null);

  // 5) Detectar fuente activa para sustitución de tildes.
  await detectActiveSubstitutions(terminal);

  // 6) Estado y render. Pasamos el perfil leído para que el selector
  //    pueda pintar el (X) en la opción correcta sin tener que leer
  //    localStorage en cada render.
  const state = createState(resolvedView, profileId, loadStoredTimeOfDay());
  state.authenticatedUser = authenticatedUser;
  state.verifyToken = tokens.verifyToken ?? null;
  state.resetToken = tokens.resetToken ?? null;
  // Si arrancamos directamente en una vista de password (entrada a
  // /recovery.html con token), hay que fijar el flag de máscara aquí:
  // createState no puede llamar a isPasswordView (vive en flows.ts) sin
  // crear un ciclo de imports con state.ts.
  state.inputIsPassword = isPasswordView(resolvedView);
  const refs = { terminal };
  const onChange = () => renderTerminal(state, refs);

  attachInputHandler({ state, refs, onChange });
  onChange();

  // 7) Datetime cada segundo (no rerender entero, solo el campo).
  window.setInterval(() => tickDatetime(refs), 1000);
}

function whenReady(fn: () => void): void {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', fn, { once: true });
  } else {
    fn();
  }
}
